import React, { Suspense, useContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { featureFlagManager } from '~/feature-flags';
import { useLocalStorage } from '~/hooks/use-local-storage';
import { safeLazy } from '~/utils/safe-lazy';

// Imports in this file should be lazy-loaded to minimize the amount
// of code we ship when the dev tools are not enabled
const DevToolsRouter = safeLazy(
  () => import('~/components/dev-tools/dev-tools-router')
);

const QUERY_PARAM_NAME = 'dev-tools';

const DevToolsContext = React.createContext();

export const useDevTools = () => {
  const [preferences, setPreferences] = useContext(DevToolsContext);

  const close = () => {
    featureFlagManager.resetAll();
    setPreferences((prev) => ({ ...prev, isOpen: false }));
    window.location.reload();
  };

  return {
    preferences,
    setPreferences,
    close,
  };
};

const DevTools = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [preferences, setPreferences] = useLocalStorage('DEV_TOOLS', {});

  useEffect(() => {
    if (searchParams.get(QUERY_PARAM_NAME) !== null) {
      searchParams.delete(QUERY_PARAM_NAME);
      setSearchParams(searchParams);

      setPreferences((prev) => ({ ...prev, isOpen: true }));
    }
  }, [searchParams, setPreferences, setSearchParams]);

  const context = useMemo(() => {
    return [preferences, setPreferences];
  }, [preferences, setPreferences]);

  return (
    <DevToolsContext.Provider value={context}>
      <Suspense fallback={null}>
        {preferences.isOpen ? <DevToolsRouter /> : null}
      </Suspense>
    </DevToolsContext.Provider>
  );
};

export default DevTools;
